import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

/**
 * Put a new page at its top.
 *
 * The menu lives in the sticky header, so it is reachable from anywhere: a
 * practitioner halfway down Add Test Results can click Add Client without
 * scrolling back up first. Nothing reset the scroll when they did, so the new
 * page arrived already scrolled to wherever the old one had been left, which
 * on a shorter page is past the end of its content and reads as the menu
 * having done nothing.
 *
 * Only Home and the PDF view handled this, each in its own way. This does it
 * once, for every route.
 */
export function shouldScrollToTopOnNavigation(
  previousPathname: string | undefined,
  pathname: string | undefined,
  action: string | undefined,
): boolean {
  /*
   * Back and forward keep their place.
   *
   * POP is the browser's own history, and a practitioner who goes back to a
   * report expects to land where they were reading it, not at the top of a
   * page they have already scrolled through once. Scrolling on POP is the
   * usual way this pattern gets added and then quietly resented.
   */
  if (action === 'POP') {
    return false;
  }
  /*
   * Same path is not a new page: it is the practitioner asking for the page
   * they are already standing on, and the containers that care about that
   * case answer it by bringing their own panel under the sticky header (see
   * reopenEntryPanelOnRenavigation, reopenAddClientPanelOnRenavigation).
   * Scrolling to zero here would fight them.
   */
  return !!pathname && previousPathname !== pathname;
}

/*
 * How long to keep putting the page back.
 *
 * One reset does not hold. React Router v5 carries the previous page's scroll
 * offset across a push, and the browser re-applies that offset after the new
 * route has rendered; async data then settles the layout again underneath it.
 * A single synchronous scrollTo is therefore undone a frame or two later and
 * the practitioner is left parked in the middle of the new page, which is the
 * bug this component was written for and then shipped with.
 *
 * So: now, on the next paint, and once more after the data has had a moment.
 * The same three-step the Add Test Results panel already uses for the same
 * reason (scrollToAddTestResultsTop in bloodwork-entry.tsx), which is where
 * this was diagnosed the first time.
 */
const SETTLE_DELAY_MS = 300;

class ScrollToTopComponent extends React.Component<RouteComponentProps, {}> {
  private unlisten?: () => void;
  private previousPathname?: string;
  private settleTimer?: number;
  private settleFrame?: number;

  /*
   * Subscribed to history rather than driven by componentDidUpdate.
   *
   * The obvious version of this component compares locations in
   * componentDidUpdate, and that only works for as long as React keeps
   * re-rendering this particular instance. Anything that remounts it instead
   * — a key, a branch in a parent, an error boundary resetting — turns every
   * navigation into a fresh mount, componentDidUpdate never runs, and the
   * page silently stops scrolling with no error to find. history.listen is
   * told about navigations directly, so it does not care how or whether React
   * re-renders anything.
   */
  componentDidMount() {
    this.previousPathname = this.props.history?.location?.pathname;
    if (!this.props.history || typeof this.props.history.listen !== 'function') {
      return;
    }
    this.unlisten = this.props.history.listen((location: any, action: any) => {
      const scroll = shouldScrollToTopOnNavigation(
        this.previousPathname,
        location?.pathname,
        // Older history versions do not hand the action to the listener.
        action ?? this.props.history?.action,
      );
      this.previousPathname = location?.pathname;
      /*
       * Always first, even when this navigation is not one we scroll for.
       * A reset scheduled by the previous navigation is about the page the
       * practitioner has just left; letting it land on the page they went
       * back to would yank them to the top of the thing they returned to
       * read, which is the one behavior the POP rule exists to prevent.
       */
      this.cancelPending();
      if (scroll) {
        this.putThePageBack();
      }
    });
  }

  private cancelPending() {
    window.clearTimeout(this.settleTimer);
    if (
      this.settleFrame !== undefined &&
      typeof window.cancelAnimationFrame === 'function'
    ) {
      window.cancelAnimationFrame(this.settleFrame);
    }
    this.settleFrame = undefined;
  }

  private putThePageBack() {
    const toTop = () => {
      // jsdom has no scrollTo, and neither do very old browsers. body and
      // documentElement are set too because which one of them is the
      // scrolling element differs by browser, and the rest of this app
      // already sets both for that reason.
      if (typeof window.scrollTo === 'function') {
        window.scrollTo(0, 0);
      }
      if (document.body) {
        document.body.scrollTop = 0;
      }
      if (document.documentElement) {
        document.documentElement.scrollTop = 0;
      }
    };

    toTop();
    if (typeof window.requestAnimationFrame === 'function') {
      this.settleFrame = window.requestAnimationFrame(toTop);
    }
    this.settleTimer = window.setTimeout(toTop, SETTLE_DELAY_MS);
  }

  componentWillUnmount() {
    this.cancelPending();
    if (this.unlisten) {
      this.unlisten();
    }
  }

  render() {
    return null;
  }
}

export const ScrollToTop = withRouter(ScrollToTopComponent);
